import qs from 'qs';
import { constant as just, isNil, omitBy } from 'lodash';
import { UserType } from '@/types/index';
import { getLanguageComponent } from './utils/i18n';

type Args<T extends any[] = any[]> = (...args: T) => string;

const localised =
  <T extends any[]>(fn: Args<T>) =>
  (locale: string, ...args: T) =>
    `/${getLanguageComponent(locale)}${fn(...args)}`;

const slugged = (path: string) => (slug: string) => `${path}/${slug}`;
const withQuery =
  <T extends Record<string, string>>(path: string) =>
  (query?: T) =>
    `${path}?${qs.stringify(omitBy(query, isNil))}`;

const routes = {
  home: (locale?: string) => (locale ? `/${getLanguageComponent(locale)}` : '/'),
  collections: localised(just('/collections')),

  // product
  product: localised(slugged('/product')),
  productOverview: localised(slugged('/product')),
  collection: localised(slugged('/collection')),
  category: localised(slugged('/category')),
  categoryGroup: localised(slugged('/all')),
  officespace: localised(just('/officespace')),

  // project
  project: localised(slugged('/project')),
  projects: localised(just('/projects')),
  story: localised(slugged('/story')),
  stories: localised(just('/stories')),

  // finishes
  finishGroupCategories: localised(just('/finishes/all')),
  finishGroupCategory: localised(slugged('/finishes')),
  finishGroup: localised(slugged('/finish')),
  ourFinishes: localised(slugged('/our-finishes')), // <- content page

  // user
  wishlist: just('/wishlist'),
  login: just('/login'),
  register: withQuery<{ allow?: string }>('/register'),
  forgotPassword: just('/forgot-password'),
  resetPassword: just('/reset-password'),
  updatePassword: just('/user/update-password'),
  upgradeAccount: withQuery<{ to: UserType }>('/user/upgrade'),

  profile: just('/user/profile'),
  security: just('/user/security'),
  estimations: just('/user/estimations'),
  downloads: just('/user/downloads'),

  // about
  ourStory: localised(just('/about/our-story')),
  flagshipStores: localised(just('/about/flagship-stores')),
  sustainability: localised(just('/about/sustainability')),
  clientSupport: localised(just('/about/client-support')),
  generalGuarantee: localised(just('/about/client-support/general-guarantee')),
  maintenance: localised(just('/about/client-support/maintenance')),
  assemblyInstructions: localised(just('/about/client-support/assembly-instructions')),
  assemblyInstructionsCollection: localised(
    slugged('/about/client-support/assembly-instructions')
  ),
  ourDesigners: localised(just('/about/our-designers')),
  designer: localised(slugged('/about/designer')),
  contact: localised(just('/about/contact')),

  // library
  resources: localised(just('/resources')),
  findYourDealer: localised(just('/find-your-dealer')),
  library: localised(just('/library')),
  catalogs: localised(just('/library/catalogs')),
  collectionLibrary: localised(slugged('/library/collection-library')),

  // press area
  pressArea: localised(just('/press-area')),
  pressRelease: localised(slugged('/press-release')),
  hrImages: localised(slugged('/hr-images')),

  legal: localised(slugged('/legal')),
  search: localised(just('/search')),
  notFound: just('/404'),
};

export default routes;
